// SettingsPurchaseOrdersPane.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Services
import { updateBase } from 'services/bases';
import {
  DEFAULT_COMPANY_SIGNATURE_TITLE,
  DEFAULT_VENDOR_SIGNATURE_TITLE,
  SIGNATURE_SHOW_AS_TEXT,
  SIGNATURE_SHOW_AS_WRITTEN,
  buildPurchaseOrderSettingsPatch,
  getPurchaseOrderSettings,
  purchaseOrderSettingsEqual,
} from 'services/bases/purchaseOrderBaseSettings';

// CSS
import './SettingsPurchaseOrdersPane.css';
import './SettingsNamePane.css';

// Components
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import SettingsModalPaneHeader from './SettingsModalPaneHeader';

const SHOW_AS_OPTIONS = Object.freeze([
  { value: SIGNATURE_SHOW_AS_TEXT, label: 'Text' },
  { value: SIGNATURE_SHOW_AS_WRITTEN, label: 'Written' },
]);

/**
 * SettingsPurchaseOrdersPane
 *
 * Signature defaults for Purchase Order document viewers.
 * Letterhead (phone/address) lives under Settings → Documents.
 */
const SettingsPurchaseOrdersPane = React.memo(({
  theme,
  base = null,
  canEditBase = false,
}) => {
  // ----- CONTEXT ----- //

  const { setActiveBase } = useBase();

  // ----- STATE ----- //

  const persisted = useMemo(() => getPurchaseOrderSettings(base), [base]);

  const [companyTitle, setCompanyTitle] = useState(persisted.companySignatureTitle);
  const [vendorTitle, setVendorTitle] = useState(persisted.vendorSignatureTitle);
  const [subtitle, setSubtitle] = useState(persisted.signatureSubtitle);
  const [showAs, setShowAs] = useState(persisted.signatureShowAs);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // ----- EFFECTS ----- //

  useEffect(() => {
    setCompanyTitle(persisted.companySignatureTitle);
    setVendorTitle(persisted.vendorSignatureTitle);
    setSubtitle(persisted.signatureSubtitle);
    setShowAs(persisted.signatureShowAs);
  }, [persisted]);

  const draft = useMemo(() => ({
    companySignatureTitle: companyTitle,
    vendorSignatureTitle: vendorTitle,
    signatureSubtitle: subtitle,
    signatureShowAs: showAs,
  }), [companyTitle, showAs, subtitle, vendorTitle]);

  const hasChanges = !purchaseOrderSettingsEqual(draft, persisted);

  const previewName = String(base?.title || 'Your Company').trim() || 'Your Company';
  const controlsDisabled = !canEditBase || isSaving;

  // ----- HANDLERS ----- //

  const clearStatus = () => {
    setErrorMessage('');
    setStatusMessage('');
  };

  const handleCompanyTitleChange = (event) => {
    setCompanyTitle(event?.target?.value || '');
    clearStatus();
  };

  const handleVendorTitleChange = (event) => {
    setVendorTitle(event?.target?.value || '');
    clearStatus();
  };

  const handleSubtitleChange = (event) => {
    setSubtitle(event?.target?.value || '');
    clearStatus();
  };

  const handleShowAsChange = (nextValue) => {
    if (controlsDisabled) return;
    setShowAs(nextValue);
    clearStatus();
  };

  const handleSave = async () => {
    if (isSaving || !base?.key || !canEditBase) return;

    setErrorMessage('');
    setStatusMessage('Saving...');
    setIsSaving(true);

    const patch = buildPurchaseOrderSettingsPatch(draft, base?.purchaseOrders);

    try {
      await updateBase(base.key, patch);
      setActiveBase((previous) => {
        if (!previous || previous.key !== base.key) return previous;
        return {
          ...previous,
          purchaseOrders: patch.purchaseOrders,
        };
      });
      setStatusMessage('Purchase order settings updated.');
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to update purchase order settings.');
      setStatusMessage('');
    } finally {
      setIsSaving(false);
    }
  };

  // ----- RENDER ----- //

  return (
    <>
      <SettingsModalPaneHeader
        title="Purchase Orders"
        subtitle="Signature defaults for purchase orders. Letterhead is under Documents."
      />

      <SettingsModalPaneContents>
        <div className="settings-purchase-orders-fields">
          <div className="settings-purchase-orders-section-label">
            Signature
          </div>

          <div className="settings-purchase-orders-field">
            <div className="settings-modal-field-label">
              Company Signature Title
            </div>
            <TextInput
              value={companyTitle}
              disabled={controlsDisabled}
              maxLength={80}
              width="100%"
              placeholder={DEFAULT_COMPANY_SIGNATURE_TITLE}
              onChange={handleCompanyTitleChange}
              onEnterPress={hasChanges ? handleSave : undefined}
            />
          </div>

          <div className="settings-purchase-orders-field">
            <div className="settings-modal-field-label">
              Vendor Signature Title
            </div>
            <TextInput
              value={vendorTitle}
              disabled={controlsDisabled}
              maxLength={80}
              width="100%"
              placeholder={DEFAULT_VENDOR_SIGNATURE_TITLE}
              onChange={handleVendorTitleChange}
              onEnterPress={hasChanges ? handleSave : undefined}
            />
          </div>

          <div className="settings-purchase-orders-field">
            <div className="settings-modal-field-label">
              Subtitle
            </div>
            <TextInput
              value={subtitle}
              disabled={controlsDisabled}
              maxLength={200}
              width="100%"
              placeholder="Add subtitle here"
              onChange={handleSubtitleChange}
              onEnterPress={hasChanges ? handleSave : undefined}
            />
          </div>

          <div className="settings-purchase-orders-field">
            <div className="settings-modal-field-label">
              Show signature as
            </div>
            <div
              className="settings-purchase-orders-show-as"
              role="radiogroup"
              aria-label="Show signature as"
            >
              {SHOW_AS_OPTIONS.map((option) => {
                const selected = showAs === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`settings-purchase-orders-show-as-option${selected ? ' is-selected' : ''}`}
                    disabled={controlsDisabled}
                    onClick={() => handleShowAsChange(option.value)}
                  >
                    <span className="settings-purchase-orders-show-as-radio" aria-hidden="true" />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="settings-purchase-orders-signature-preview">
              {showAs === SIGNATURE_SHOW_AS_WRITTEN ? (
                <div className="settings-purchase-orders-signature-preview-written">
                  Written signatures will use a drawing pad when signing.
                </div>
              ) : (
                <div className="settings-purchase-orders-signature-preview-name">
                  {previewName}
                </div>
              )}
              <div className="settings-purchase-orders-signature-preview-line" aria-hidden="true" />
            </div>
          </div>

          {canEditBase ? (
            <div className="settings-purchase-orders-actions">
              <Button
                iconKey="save"
                label="Save"
                enabled={hasChanges && !isSaving}
                onClick={handleSave}
              />
            </div>
          ) : null}
        </div>

        {!canEditBase && (
          <div
            className="settings-modal-note"
            style={{
              color: theme.foregroundMuted,
            }}
          >
            Only admins and owners can update purchase order settings.
          </div>
        )}

        {statusMessage && !errorMessage ? (
          <div className="settings-modal-status">{statusMessage}</div>
        ) : null}

        {errorMessage ? (
          <div className="settings-modal-error">{errorMessage}</div>
        ) : null}
      </SettingsModalPaneContents>
    </>
  );
});

SettingsPurchaseOrdersPane.displayName = 'SettingsPurchaseOrdersPane';

export default SettingsPurchaseOrdersPane;
